import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

// Global region interceptor — adds X-Portal-Region header on every API request.
// Region is derived from the current URL path (/in/* → IN, /uae/* → UAE).
// localStorage is used as a fallback ONLY for non-partner routes; partner flows
// must rely on explicit URL prefix to avoid cross-region poisoning.
const resolvePortalRegion = () => {
  try {
    // Highest priority: the authenticated user's bound region (stored at login).
    // This prevents the "Indian customer on /properties/<id> sends X-Portal-Region=UAE
    // and gets 404 on Indian property" bug — the URL prefix is not always present.
    const userRegion = (typeof localStorage !== 'undefined' && localStorage.getItem('user_region')) || '';
    if (userRegion === 'IN' || userRegion === 'UAE') return userRegion;

    const path = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
    const segments = path.split('/').filter(Boolean);
    const first = segments[0] || '';
    if (first === 'in' || first === 'india') return 'IN';
    if (first === 'uae' || first === 'dubai') return 'UAE';
    // Partner routes (e.g. /partner/login) always default to UAE — never read
    // localStorage, since prior country-switcher clicks would otherwise poison the region.
    if (first === 'partner') return 'UAE';
    const saved = (typeof localStorage !== 'undefined' && localStorage.getItem('dreamlife_country')) || '';
    if (saved.toLowerCase() === 'india') return 'IN';
    if (saved.toLowerCase() === 'uae') return 'UAE';
  } catch { /* ignore */ }
  return 'UAE';
};

// Helper: keep localStorage `user_region` in sync with the cookie-auth'd user.
// Called from initAuth + login + signup. Logout clears it.
const persistUserRegion = (userData) => {
  try {
    if (typeof localStorage === 'undefined') return;
    const r = (userData?.region || '').toUpperCase();
    if (r === 'IN' || r === 'UAE') localStorage.setItem('user_region', r);
    else localStorage.removeItem('user_region');
  } catch { /* ignore */ }
};

axios.interceptors.request.use((config) => {
  // Only add header for requests targeting our own backend
  if (config.url && config.url.startsWith(BACKEND_URL)) {
    config.headers = config.headers || {};
    config.headers['X-Portal-Region'] = resolvePortalRegion();
    // Auto-attach the partner Bearer token (sessionStorage) so partner pages
    // survive a page reload / direct-URL navigation. Skip if the caller
    // already provided an Authorization header (e.g. customer cookie flow).
    if (!config.headers.Authorization && !config.headers.authorization) {
      try {
        const partnerToken = (typeof sessionStorage !== 'undefined')
          ? sessionStorage.getItem('partner_session')
          : null;
        if (partnerToken) {
          config.headers.Authorization = `Bearer ${partnerToken}`;
        }
      } catch { /* ignore */ }
    }
  }
  return config;
}, (error) => Promise.reject(error));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch { /* ignore network errors — we still clear locally below */ }
    // Hard reset: nuke BOTH the customer state AND any partner session
    // bleed-over from this browser. Prevents the "previous user keeps coming
    // back" bug where a stale partner_session in sessionStorage was being
    // auto-attached to subsequent requests via the axios interceptor.
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('partner_session');
        sessionStorage.removeItem('partner_user');
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('partner_country');
        localStorage.removeItem('user_region');
      }
    } catch { /* ignore */ }
    setUser(null);
  }, []);

  const initAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      persistUserRegion(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    persistUserRegion(userData);
    return userData;
  }, []);

  const signup = useCallback(async (name, email, password) => {
    const response = await axios.post(`${API}/auth/signup`, { name, email, password });
    const { user: userData } = response.data;
    setUser(userData);
    persistUserRegion(userData);
    return userData;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
